import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { celebrate, checkAchievements } from "../lib/achievements";
import {
  RefreshCcw, Star, StarHalf, Play, Eye, ChevronRight, CheckCircle2, Target,
  AlertTriangle, HeartPulse, Clock, Brain, Sparkles, PartyPopper,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const CATEGORY_META = {
  recovery: { label: "Recovery", cls: "bg-terracotta-500/12 text-terracotta-600 border-terracotta-500/30", icon: HeartPulse },
  overdue: { label: "Overdue", cls: "bg-amber-500/12 text-amber-700 border-amber-500/30", icon: AlertTriangle },
  recent: { label: "Recent memorization", cls: "bg-forest-600/10 text-forest-700 border-forest-600/25", icon: Clock },
  long_term: { label: "Long-term revision", cls: "bg-gold-500/12 text-gold-700 border-gold-500/30", icon: RefreshCcw },
};

const RATINGS = [
  { label: "Perfect", accuracy: 98, cls: "bg-forest-600 text-sand-100 hover:bg-forest-700" },
  { label: "Good", accuracy: 95, cls: "bg-gold-500 text-foreground hover:bg-gold-600" },
  { label: "Shaky", accuracy: 70, cls: "bg-amber-200 text-amber-900 hover:bg-amber-300" },
  { label: "Forgot", accuracy: 40, cls: "bg-terracotta-500 text-white hover:bg-terracotta-600" },
];

export function StrengthStars({ value, size = "h-3.5 w-3.5" }) {
  const { t } = useLanguage();
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-0.5" title={t("revision.memoryStrength5", { value })}>
      {[0, 1, 2, 3, 4].map((i) => {
        if (i < full) return <Star key={i} className={`${size} fill-gold-500 text-gold-500`} />;
        if (i === full && half) return <StarHalf key={i} className={`${size} fill-gold-500 text-gold-500`} />;
        return <Star key={i} className={`${size} text-sand-300`} />;
      })}
    </span>
  );
}

export default function Revision() {
  const { tx, t } = useLanguage();
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);
  const [goals, setGoals] = useState(null);
  const [session, setSession] = useState(null); // {items, idx, revealed, done}

  const load = () => {
    api.get("/revision/queue").then((r) => setData(r.data)).catch(() => {});
    api.get("/plan/today").then((r) => setPlan(r.data)).catch(() => {});
    api.get("/goals").then((r) => setGoals(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const queue = data?.queue || [];
  const counts = queue.reduce((acc, q) => ({ ...acc, [q.category]: (acc[q.category] || 0) + 1 }), {});

  const startSession = () => setSession({ items: queue, idx: 0, revealed: false, done: 0 });

  const rate = async (accuracy) => {
    const item = session.items[session.idx];
    try {
      const r = await api.post("/revision/complete", { surah: item.surah, ayah: item.ayah, accuracy });
      toast.success(r.data.message);
      const nextIdx = session.idx + 1;
      if (nextIdx >= session.items.length) {
        setSession({ ...session, idx: nextIdx, done: session.done + 1, finished: true });
        celebrate();
        checkAchievements();
        load();
      } else {
        setSession({ ...session, idx: nextIdx, revealed: false, done: session.done + 1 });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("revision.couldnTSaveReview")); }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="revision-page">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("revision.aiHifzCoach")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("revision.revision")}</h1>
        {plan?.coach_message && (
          <div className="mt-3 rounded-xl border-l-4 border-gold-500 bg-gold-500/8 bg-sand-200/60 p-4 text-foreground/85 flex gap-3" data-testid="coach-message">
            <Sparkles className="h-5 w-5 shrink-0 text-gold-600 mt-0.5" />
            <span className="leading-relaxed">{plan.coach_message}</span>
          </div>
        )}
      </header>

      {/* Stats chips */}
      <div className="flex flex-wrap gap-2" data-testid="revision-stats">
        {Object.entries(CATEGORY_META).map(([k, m]) => (
          <span key={k} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${m.cls}`} data-testid={`revision-chip-${k}`}>
            <m.icon className="h-3.5 w-3.5" /> {tx(m.label)}: {counts[k] || 0}
          </span>
        ))}
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-sand-300 bg-white text-foreground/70">
          {t("revision.scheduledAyahs", { n: data?.total_scheduled ?? "…" })}
        </span>
      </div>

      {/* Guided session */}
      {session ? (
        <GuidedSession session={session} rate={rate} setRevealed={(v) => setSession({ ...session, revealed: v })} exit={() => { setSession(null); load(); }} />
      ) : (
        <>
          {/* Start button */}
          <section className="hifz-card grain bg-forest-700 border-forest-700 text-sand-100" data-testid="revision-start-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-display font-semibold text-2xl">{t("revision.todaySRevision")}</div>
                <p className="mt-1 text-sm text-sand-100/75">
                  {queue.length === 0
                    ? t("revision.allCaughtUpNothingDue")
                    : t("revision.queuedPrioritized", { count: queue.length })}
                </p>
              </div>
              {queue.length > 0 && (
                <button onClick={startSession} className="btn-accent" data-testid="start-revision-btn">
                  <Play className="h-4 w-4" /> {t("revision.startTodaySRevision")}
                </button>
              )}
            </div>
          </section>

          {/* Queue list */}
          {queue.length > 0 && (
            <section className="hifz-card" data-testid="revision-queue">
              <h2 className="font-display font-semibold text-xl text-forest-700">{t("revision.queue")}</h2>
              <div className="mt-4 divide-y divide-sand-300">
                {queue.map((q) => {
                  const m = CATEGORY_META[q.category];
                  return (
                    <div key={`${q.surah}-${q.ayah}`} className="py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`queue-item-${q.surah}-${q.ayah}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${m.cls}`}>
                          <m.icon className="h-3 w-3" /> {tx(m.label)}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{t("revision.ayah", { surah_name: q.surah_name, ayah: q.ayah })}</div>
                          <div className="text-xs text-muted-foreground">
                            {q.days_overdue > 0 ? `${t("revision.daysOverdue", { count: q.days_overdue })} · ` : ""}
                            {t("revision.reviewsDone", { count: q.history?.length || 0 })}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StrengthStars value={q.strength} />
                        <Link to={`/app/hifz-test?mode=whisper&surah=${q.surah}&ayah=${q.ayah}&ayah_end=${q.ayah}`} className="btn-ghost text-xs px-2.5 py-1.5" data-testid={`queue-aitest-${q.surah}-${q.ayah}`}>
                          <Brain className="h-3.5 w-3.5" /> {t("revision.aiTest")}
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </>
      )}

      {/* Goal editor */}
      {goals && <GoalCard goals={goals} onSaved={load} />}
    </div>
  );
}

function GuidedSession({ session, rate, setRevealed, exit }) {
  const { tx, t } = useLanguage();
  if (session.finished) {
    return (
      <section className="hifz-card p-10 text-center" data-testid="session-complete">
        <PartyPopper className="h-10 w-10 mx-auto text-gold-500" />
        <h2 className="mt-4 font-display font-semibold text-2xl text-forest-700">{t("revision.revisionComplete")}</h2>
        <p className="mt-2 text-foreground/70">{t("revision.sessionDone", { count: session.done })}</p>
        <button onClick={exit} className="btn-primary mt-6" data-testid="session-exit-btn">{t("revision.backToQueue")}</button>
      </section>
    );
  }
  const item = session.items[session.idx];
  const m = CATEGORY_META[item.category];
  return (
    <section className="hifz-card p-8" data-testid="guided-session">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">{t("revision.reviewingOf", { n: session.idx + 1, total: session.items.length })}</div>
        <button onClick={exit} className="text-xs text-foreground/60 hover:underline" data-testid="session-quit-btn">{t("revision.exitSession")}</button>
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-sand-200 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-forest-600 to-gold-500 transition-all duration-500" style={{ width: `${(session.idx / session.items.length) * 100}%` }} />
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${m.cls}`}>
          <m.icon className="h-3.5 w-3.5" /> {tx(m.label)}
        </span>
        <span className="font-display font-semibold text-forest-700">{t("revision.ayah", { surah_name: item.surah_name, ayah: item.ayah })}</span>
        <StrengthStars value={item.strength} />
      </div>

      {!session.revealed ? (
        <div className="mt-8 text-center py-8" data-testid="session-recall">
          <div className="font-display text-xl text-forest-700">{t("revision.reciteThisAyahFromMemory")}</div>
          <p className="mt-2 text-sm text-muted-foreground">{t("revision.takeYourTimeReciteOut")}</p>
          <button onClick={() => setRevealed(true)} className="btn-primary mt-6" data-testid="session-reveal-btn">
            <Eye className="h-4 w-4" /> {t("revision.revealAyah")}
          </button>
        </div>
      ) : (
        <div className="mt-6" data-testid="session-revealed">
          <div className="rounded-xl bg-sand-200/50 p-6">
            <div className="ayah-arabic text-2xl sm:text-3xl leading-[2.2] text-right text-forest-700" dir="rtl">
              {item.ar} <span className="text-gold-600">﴿{item.ayah}﴾</span>
            </div>
            <p className="mt-4 text-sm text-foreground/75 leading-relaxed">{item.en}</p>
          </div>
          <div className="mt-5 text-sm text-muted-foreground text-center">{t("revision.howDidItGo")}</div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            {RATINGS.map((r) => (
              <button key={r.label} onClick={() => rate(r.accuracy)}
                className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${r.cls}`}
                data-testid={`session-rate-${r.label.toLowerCase()}`}>
                {tx(r.label)}
              </button>
            ))}
          </div>
          <div className="mt-3 text-center">
            <Link to={`/app/hifz-test?mode=whisper&surah=${item.surah}&ayah=${item.ayah}&ayah_end=${item.ayah}`} className="text-xs text-forest-700 hover:underline" data-testid="session-aitest-link">
              {t("revision.orTakeTheAiRecitation")}
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

const GOAL_PRESETS = [
  { goal_type: "ayahs", goal_value: 3, label: "3 ayahs" },
  { goal_type: "ayahs", goal_value: 5, label: "5 ayahs" },
  { goal_type: "page_fraction", goal_value: 0.25, label: "¼ page" },
  { goal_type: "page_fraction", goal_value: 0.5, label: "½ page" },
  { goal_type: "page_fraction", goal_value: 0.75, label: "¾ page" },
  { goal_type: "pages", goal_value: 1, label: "1 page" },
  { goal_type: "minutes", goal_value: 20, label: "20 min" },
  { goal_type: "minutes", goal_value: 30, label: "30 min" },
];

function GoalCard({ goals, onSaved }) {
  const { tx, t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [customAyahs, setCustomAyahs] = useState("");
  const isCurrent = (p) => p.goal_type === goals.goal_type && Math.abs(p.goal_value - goals.goal_value) < 0.001;

  const save = async (goal_type, goal_value) => {
    setSaving(true);
    try {
      const r = await api.put("/goals", { goal_type, goal_value });
      toast.success(t("revision.dailyGoalUpdatedAboutAyahs", { daily_ayahs: r.data.daily_ayahs }));
      onSaved();
    } catch { toast.error(t("revision.couldnTUpdateGoal")); }
    finally { setSaving(false); }
  };

  const rec = goals.recommendation;
  const recLabel = rec.goal_type === "page_fraction" ? `${rec.goal_value === 0.5 ? "½" : rec.goal_value === 0.25 ? "¼" : "¾"} page`
    : rec.goal_type === "pages" ? `${rec.goal_value} page${rec.goal_value === 1 ? "" : "s"}`
    : rec.goal_type === "minutes" ? `${rec.goal_value} minutes` : `${rec.goal_value} ayahs`;
  const recIsCurrent = rec.goal_type === goals.goal_type && Math.abs(rec.goal_value - goals.goal_value) < 0.001;

  return (
    <section className="hifz-card" data-testid="goal-card">
      <h2 className="font-display font-semibold text-xl text-forest-700 inline-flex items-center gap-2">
        <Target className="h-5 w-5" /> {t("revision.dailyGoal")}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t("revision.currentlyAbout")} <strong className="text-forest-700">{t("revision.ayahs", { daily_ayahs: goals.daily_ayahs })}</strong> {t("revision.ofNewMemorizationPerDay")}
      </p>

      {!recIsCurrent && (
        <div className="mt-4 rounded-xl border border-gold-500/40 bg-gold-500/8 bg-sand-200/50 p-4" data-testid="goal-recommendation">
          <div className="text-xs uppercase tracking-[0.18em] text-gold-700 font-semibold">{t("revision.coachRecommendsDay", { recLabel })}</div>
          <p className="mt-1.5 text-sm text-foreground/80 leading-relaxed">{rec.reason}</p>
          <button onClick={() => save(rec.goal_type, rec.goal_value)} disabled={saving} className="btn-accent text-xs mt-3" data-testid="goal-accept-rec">
            <CheckCircle2 className="h-3.5 w-3.5" /> {t("revision.acceptRecommendation")}
          </button>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2">
        {GOAL_PRESETS.map((p) => (
          <button key={p.label} onClick={() => save(p.goal_type, p.goal_value)} disabled={saving}
            className={`rounded-xl px-3 py-2.5 text-sm font-medium border transition ${
              isCurrent(p) ? "bg-forest-600 text-sand-100 border-forest-600" : "border-sand-300 hover:bg-sand-200"
            }`}
            data-testid={`goal-preset-${p.goal_type}-${String(p.goal_value).replace(".", "_")}`}>
            {tx(p.label)}
          </button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <input type="number" min="1" max="50" value={customAyahs} onChange={(e) => setCustomAyahs(e.target.value)}
          placeholder={t("revision.customAyahs")} className="rounded-xl border border-sand-300 bg-white px-3 py-2 text-sm w-36" data-testid="goal-custom-input" />
        <button onClick={() => customAyahs && save("ayahs", parseInt(customAyahs))} disabled={saving || !customAyahs}
          className="btn-ghost text-sm disabled:opacity-50" data-testid="goal-custom-save">{t("revision.set")}</button>
      </div>
    </section>
  );
}
